import React, { useState, useCallback, useEffect } from 'react';
import { Text, Group } from '@mantine/core';
import type { IConfiguracionState } from '../../interfaces';
import { EstadoInicialConfiguracion, variablesObligatoriasCompletas } from '../../interfaces';
import { FormularioVariablesGlobalesVista } from '../../vista/formularioVariablesGlobalesVista';
import { BarraCambiosSinGuardarVista } from '../../vista/barraCambiosSinGuardarVista';
import { useNotificaciones } from '../../../../globales/contextos/notificationContext';
import { useAvisoErrorConsulta } from '../useAvisoErrorConsulta';
import { usePaisConfiguracion } from '../paisConfiguracion';
import { useMonedaPais } from '../useMonedaPais';
import { HistorialCambiosVariablesCV } from '../historialCambiosVariablesCV';
import { CotizadorModelo } from '../../../../modelos/CotizadorNBS';
import { EnumTipoNotificacion } from '../../../../globales/interfaces';

interface VariablesGlobalesCVProps {
  configInicial?: IConfiguracionState;
}

// Función auxiliar para mapear el response del backend (camelCase) a PascalCase
const mapToPascalCase = (obj: any): any => {
  if (!obj) return obj;
  const mapped: any = {};
  Object.keys(obj).forEach(key => {
    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
    mapped[pascalKey] = obj[key];
  });
  return mapped;
};

/** Quién guardó la versión vigente y cuándo (en lugar del número de versión, que no le dice nada al usuario). */
interface IUltimaActualizacion { fecha: string; usuario?: string }

const formatoFecha = (utc: string) =>
  new Date(utc.endsWith('Z') ? utc : `${utc}Z`).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });

export const VariablesGlobalesCV: React.FC<VariablesGlobalesCVProps> = ({
  configInicial = EstadoInicialConfiguracion
}) => {
  const [config, setConfig] = useState<IConfiguracionState>(configInicial);
  const [configOriginal, setConfigOriginal] = useState<IConfiguracionState>(configInicial);
  const [ultimaActualizacion, setUltimaActualizacion] = useState<IUltimaActualizacion | null>(null);
  const [guardando, setGuardando] = useState(false);
  const { mostrarNotificacion } = useNotificaciones();
  const paisId = usePaisConfiguracion();
  const avisarErrorConsulta = useAvisoErrorConsulta();
  const { monedaLocal, usaTrm } = useMonedaPais(paisId);

  const cargarConfiguracion = useCallback(async () => {
    try {
      if (paisId) {
        const modelo = new CotizadorModelo();
        const resConfig = await modelo.obtenerConfiguracionGlobal(paisId);
        if (resConfig?.Data) {
          const mappedData = mapToPascalCase(resConfig.Data);
          setConfig(mappedData);
          setConfigOriginal(mappedData);
          setUltimaActualizacion(mappedData.FechaCreacion ? { fecha: mappedData.FechaCreacion, usuario: mappedData.CreadoPor } : null);
        }
      }
    } catch (error) {
      avisarErrorConsulta(error, 'la configuración global',
        'Tu país todavía no tiene configuración global: diligencia las variables y guárdalas.');
    }
  }, [paisId, avisarErrorConsulta]);

  useEffect(() => { cargarConfiguracion(); }, [cargarConfiguracion]);

  const alCambiar = useCallback((key: keyof IConfiguracionState, value: number | string | null) => {
    setConfig(prev => ({
      ...prev,
      // null: campo opcional vacío (plazos y garantías de la oferta).
      [key]: value === null ? null : Number(value)
    }));
  }, []);

  const alGuardar = async () => {
    if (!variablesObligatoriasCompletas(config, usaTrm)) {
      mostrarNotificacion(
        EnumTipoNotificacion.Warning,
        'Campos Obligatorios',
        'Debes diligenciar todos los campos obligatorios antes de guardar.'
      );
      return;
    }

    setGuardando(true);
    try {
      const modelo = new CotizadorModelo();
      const payload = {
        ...config,
        Id: 0,
        paisId: paisId,
        esActiva: true
      };
      const res = await modelo.guardarConfiguracionGlobal(payload);

      if (res?.NotificationType === 1) { // Success
        mostrarNotificacion(
          EnumTipoNotificacion.Success,
          'Configuración Guardada',
          'Los parámetros globales se han actualizado correctamente.'
        );
        // Se vuelve a consultar para mostrar la versión recién guardada (quién y cuándo).
        await cargarConfiguracion();
      } else {
        mostrarNotificacion(
          EnumTipoNotificacion.Error,
          'Error',
          res?.Messages?.[0] || 'No se pudo guardar la configuración.'
        );
      }
    } catch (error: any) {
      console.error(error);
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error',
        error?.mensajeBackend || 'Ocurrió un error inesperado al guardar la configuración.'
      );
    } finally {
      setGuardando(false);
    }
  };

  const haCambiado = JSON.stringify(config) !== JSON.stringify(configOriginal);

  return (
    <div style={{ flex: 1, paddingBottom: '20px' }}>
      <Group justify="space-between" align="center" mb="md" gap="xs">
        <Text size="xs" c="dimmed">
          {ultimaActualizacion
            ? `Última actualización: ${formatoFecha(ultimaActualizacion.fecha)}${ultimaActualizacion.usuario ? ` · ${ultimaActualizacion.usuario}` : ''}`
            : ''}
        </Text>
        {paisId && (
          <HistorialCambiosVariablesCV
            subtitulo="Configuración global"
            monedaLocal={monedaLocal}
            cargar={async () => (await new CotizadorModelo().obtenerCambiosConfiguracionGlobal(paisId as number)).Data ?? []}
          />
        )}
      </Group>

      <FormularioVariablesGlobalesVista config={config} alCambiar={alCambiar} monedaLocal={monedaLocal} usaTrm={usaTrm} mostrarTopes />

      {/* Aparece solo con cambios sin guardar, fija abajo: se guarda sin volver arriba. */}
      <BarraCambiosSinGuardarVista
        visible={haCambiado}
        alDescartar={() => setConfig(configOriginal)}
        alGuardar={alGuardar}
        guardando={guardando}
      />
    </div>
  );
};
